import { Routes, Route, Navigate } from "react-router-dom";
import ProtectedRoute from "./components/ProtectedRoute";
import { lazy } from "react";
import { useStore } from "./store/store";
// Lazy loading components
const Dashboard = lazy(() => import("./archive/pages/Dashboard"));
const KnowledgeStore = lazy(
  () => import("./journeys/knowledge/KnowledgeStore"),
);
const FeedbackReport = lazy(
  () => import("./journeys/feedback-report/FeedbackReport"),
);
const Chat = lazy(() => import("./journeys/chat/Chat"));
const GroupManagement = lazy(
  () => import("./journeys/group-management/GroupManagement"),
);
const UserAdoptionInsights = lazy(
  () => import("./journeys/user-adoption-insights/UserAdoptionInsights"),
);
const UserManagement = lazy(
  () => import("./journeys/user-management/UserManagement"),
);
const Agents = lazy(() => import("./journeys/agents/Agent"));
const PromptTemplate = lazy(
  () => import("./journeys/prompt-template/PromptTemplate"),
);
const Login = lazy(() => import("./journeys/login/Login"));
const QueryLibrary = lazy(
  () => import("./journeys/query-library/QueryLibrary"),
);
const Performance = lazy(() => import("./journeys/performance/Performance"));
const RAIMetrics = lazy(() => import("./journeys/rai-metrics/RAIMetrics"));
const NotFound = lazy(() => import("./archive/pages/NotFound"));
const Unauthorized = lazy(() => import("./archive/pages/Unauthorized"));
const Organizations = lazy(
  () => import("./journeys/organizations/Organizations"),
);
const PromptGuidance = lazy(
  () => import("./journeys/promt-guidance/PromptGuidance"),
);
const Settings = lazy(() => import("./journeys/settings/Settings"));
const Explainability = lazy(
  () => import("./journeys/explainability/Explainability"),
);

const protectedRoutes = [
  {
    path: "/dashboard",
    element: <Dashboard />,
    permission: "access_playground",
  },
  {
    path: "/knowledge-store",
    element: <KnowledgeStore />,
    permission: "knowledge_store",
  },
  {
    path: "/prompt-template",
    element: <PromptTemplate />,
    permission: "prompt_templates",
  },
  { path: "/users", element: <UserManagement />, permission: "manage_users" },
  {
    path: "/groups",
    element: <GroupManagement />,
    permission: "manage_groups",
  },
  { path: "/chat", element: <Chat />, permission: "chat_with_personas" },
  // { path: "/agents", element: <Agent /> }, // no permission required to access agents page
  { path: "/metrics", element: <RAIMetrics />, permission: "view_metrics" },
  {
    path: "/query-library",
    element: <QueryLibrary />,
    permission: "query_library",
  },
  {
    path: "/performance-dashboard",
    element: <Performance />,
    permission: "performance_dashboard",
  },
  {
    path: "/user-adoption-insights",
    element: <UserAdoptionInsights />,
    permission: "user_adoption_insights",
  },
  {
    path: "/feedback-report",
    element: <FeedbackReport />,
    permission: "feedback_report",
  },
  { path: "/settings", element: <Settings />, permission: "settings" },
  { path: "/agents", element: <Agents />, permission: "access_agents" },
  {
    path: "/organizations",
    element: <Organizations />,
    permission: "manage_orgs",
  },
  {
    path: "/prompt-guidance",
    element: <PromptGuidance />,
    permission: "prompt_guidance",
  },
  {
    path: "/explainability",
    element: <Explainability />,
    permission: "explainability",
  },
];

const AuthRedirect = () => {
  const isAuthenticated = useStore((s) => s.auth.state.isAuthenticated);
  const initialized = useStore((s) => s.auth.initialized);

  if (!initialized) return null;

  return isAuthenticated ? (
    <Navigate to="/chat" replace />
  ) : (
    <Navigate to="/login" replace />
  );
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useStore((s) => s.auth.state.isAuthenticated);
  const initialized = useStore((s) => s.auth.initialized);

  if (!initialized) return null;

  if (isAuthenticated) {
    return <Navigate to="/chat" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Redirect root to dashboard */}
      <Route path="/" element={<AuthRedirect />} />

      {/* Public routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />

      <Route path="/unauthorized" element={<Unauthorized />} />

      {/* Protected routes */}
      {protectedRoutes.map(({ path, element, permission }) => (
        <Route
          key={path}
          path={path}
          element={
            <ProtectedRoute requiredPermission={permission}>
              {element}
            </ProtectedRoute>
          }
        />
      ))}

      {/* Catch-all 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
